import { createElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import { useQuery } from '@tanstack/react-query';
import { areTreesEqual } from '@kizen/page-builder/utils/are-equal';
import FullscreenModal from 'components/Modals/Fullscreen';
import {
  ConfirmExitModal,
  PageBuilderCanvas,
  PageBuilderContainer,
  PageBuilderHeader,
  PageBuilderTray,
} from 'components/PageBuilder';
import { useToast, toastVariant } from 'components/ToastProvider';
import {
  emailCreatorElements,
  emailNodes,
  emailNodeSettings,
  emailTrayItems,
} from 'components/PageBuilder/presets';
import { usePathSetter } from 'hooks/usePathSetter';
import { useModalControl } from 'hooks/useModalControl';
import { useFlashTransition } from 'hooks/useFlashState';
import LibraryService from 'services/LibraryService';
import TeamMemberService from 'services/TeamMemberService';
import { getChosenBusiness } from 'store/authentication/selectors';
import {
  SenderType,
  SendFrom,
  SendTestEmailButton,
  CUSTOM,
  DEFAULT,
  SENDER_TYPE,
  IntegratedInboxSenderType,
} from './components';
import {
  PageBuilderBodyHeader,
  PageBuilderContentWrapper,
  PageBuilderCanvasWrapper,
  SenderInfoContainer,
  StyledSubjectLine,
  StyledInternalName,
  StyledCustomName,
  StyledFromName,
  StyledIntegratedInbox2,
} from './styled';
import {
  buildResponse,
  cleanCraftJsContent,
  getCraftJsonWithSafeDynamicImageIds,
  getMjmlJson,
  getSelectedSummary,
  getTeamMemberValue,
} from './utils';
import { useMjml2Html } from '@kizen/mjml/hooks';
import { convertToTeamOption } from 'utility/TransformToSelectOptions';
import { SendFromTeamMemberField } from './components/SendFromTeamMemberField';
import { displayNameToOption } from 'services/helpers';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import { maybeTrackAIRequest } from 'utility/analytics';
import { Entities, useSelectTypeaheadWithScroll } from '../Inputs/Select/hooks';
import { useEmailFromDomainEnabledEntitlement } from 'hooks/useEmailFromDomainEnabledEntitlement';
import { useIntegratedInbox } from 'hooks/useIntegratedInbox';
import { getInvalidLabel } from 'pages/Account/pages/Profile/Forms/EmailIntegration';
import { useMergeFields } from 'components/WYSIWYG/use-merge-fields';
import { useMergeFieldModalContext } from 'components/WYSIWYG/components/MergeFieldModal';
import { collectMergeFields } from '__components/WYSIWYG/editor';
import { displayNamesQuery } from 'query/records';
import { isMergeField } from 'utility/merge-fields';

const {
  LAST_ROLE,
  TEAM_MEMBER,
  BUSINESS,
  LAST_ANY,
  TEAM_MEMBER_FIELD,
  SPECIFIC_INTEGRATED_INBOX,
} = SENDER_TYPE;

const EMPTY_ARRAY = [];

export const useTextLinkOptions = (t, isForCustomObject) =>
  useMemo(
    () => [
      {
        label: isForCustomObject
          ? t('Entity Record Link')
          : t('Contact Record Link'),
        href: `{{ ${isForCustomObject ? 'entity_record' : 'contact'}.link_url }}`,
      },
    ],
    [t, isForCustomObject]
  );

const headerDataEq = (h1, h2) => {
  const k1 = Object.keys(h1);
  const k2 = Object.keys(h2);

  if (k1.length !== k2.length) {
    return false;
  }

  for (const key of k1) {
    if (!h1[key] && !k2[key]) {
      continue;
    }

    if (h1[key] !== h2[key]) {
      return false;
    }
  }

  return true;
};

export const MessageBuilder = ({
  initialData = {},
  onConfirm,
  onCancel,
  modalLayer = 0,
  readonlySenderInfo = false,
  isTemplateUpdating = false,
  mergeFields: mergeFieldsProp = null,
  loadingMergeFields: loadingMergeFieldsProp,
  customObject = null,
  useAllIntgegratedInboxes = false,
  enableAllObjectMergeFields = false,
  customObjectName,
}) => {
  const canvasRef = useRef();
  const sendFromSelectFrom = useRef();
  const {
    sender,
    subject,
    name,
    craftJsContent,
    craftJson,
    senderTeamMember,
    externalAccount,
  } = initialData || {};

  const content = cleanCraftJsContent(craftJson || craftJsContent);
  const dataRef = useRef(getCraftJsonWithSafeDynamicImageIds(content)); // reassign ids here so the unsaved changes logic is accurate
  const currentEditsRef = useRef(dataRef.current);
  const { name: chosenBusinessName, id: businessId } =
    useSelector(getChosenBusiness) || {};
  const default_integrated_inbox = useSelector(
    (s) => s.authentication.chosenBusiness.default_integrated_inbox
  );
  const businessVariables = useSelector((store) => {
    return store.authentication.chosenBusiness?.business_variables || [];
  });

  const enableOwnMergeFieldsHook =
    !enableAllObjectMergeFields && !mergeFieldsProp;

  const mergeFieldModalContext = useMergeFieldModalContext();

  const { data: mergeFields, isLoading: loadingOwnMergeFields } =
    useMergeFields({
      enabled: enableOwnMergeFieldsHook,
      listHeaders: true,
      mergeFields: mergeFieldsProp,
    });

  const mergeFieldData = useMemo(() => {
    if (!craftJson) {
      return {
        automationVariables: [],
        businessVariables: [],
        customFields: [],
        originalMergeFields: [],
      };
    }

    const textNodes = Object.values(craftJson).filter((node) => {
      return node?.type?.resolvedName === 'Text';
    });
    const originalMergeFields = textNodes.flatMap((node) => {
      const mergeFields = collectMergeFields(node.custom.text);
      return mergeFields;
    });

    const mergeFields = originalMergeFields.reduce((acc, mf) => {
      if (isMergeField.linkUrlField(mf)) {
        return acc;
      } else if (isMergeField.contact(mf)) {
        acc.push(mf.replace('contact.', 'client_client.'));
      } else if (customObjectName && isMergeField.entityRecord(mf)) {
        acc.push(mf.replace('entity_record.', `${customObjectName}.`));
      } else {
        acc.push(mf);
      }
      return acc;
    }, []);

    const automationVariables = mergeFields.filter(
      isMergeField.automationVariable
    );
    const businessVariables = mergeFields.filter(isMergeField.businessVariable);
    const customFields = mergeFields.filter((mf) => {
      return (
        !isMergeField.automationVariable(mf) &&
        !isMergeField.businessVariable(mf) &&
        !isMergeField.staticBusinessVariable(mf) &&
        !isMergeField.teamMember(mf)
      );
    });

    return {
      automationVariables,
      businessVariables,
      customFields,
      originalMergeFields,
    };
  }, [craftJson, customObjectName]);

  const notFoundBusinessMergeFields = useMemo(() => {
    const result = [];
    const referencedVariables = mergeFieldData.businessVariables
      .filter(isMergeField.businessVariable)
      .map((mf) => mf.split('.').pop());
    const existingVariables = businessVariables.map((bv) => bv.api_name);

    for (const name of referencedVariables) {
      if (!existingVariables.includes(name)) {
        result.push(`business_variable.${name}`);
      }
    }

    return result;
  }, [mergeFieldData.businessVariables, businessVariables]);

  const notFoundAutomationMergeFields = useMemo(() => {
    if (!mergeFieldModalContext.enabled.automationVariable) {
      return [];
    }

    const result = [];
    const referencedVariables = mergeFieldData.automationVariables
      .filter(isMergeField.automationVariable)
      .map((mf) => mf.split('.').pop());
    const existingVariables = mergeFieldModalContext.automationVariables.map(
      (av) => {
        return av.value;
      }
    );

    for (const name of referencedVariables) {
      if (!existingVariables.includes(name)) {
        result.push(`automation_variable.${name}`);
      }
    }

    return result;
  }, [
    mergeFieldData.automationVariables,
    mergeFieldModalContext.automationVariables,
    mergeFieldModalContext.enabled.automationVariable,
  ]);

  const notFoundMergeFieldsQuery = useQuery(
    displayNamesQuery(
      { custom_field_names: mergeFieldData.customFields },
      {
        enabled: mergeFieldData.customFields.length > 0,
        staleTime: 0,
        gcTime: 0,
        select: (data) => {
          return data.not_found.custom_fields.map((dn) => {
            if (dn.id.startsWith('client_client.')) {
              return dn.id.replace('client_client.', 'contact.');
            }
            if (customObjectName) {
              const fieldName = dn.id.split('.').pop();

              if (
                mergeFieldData.originalMergeFields.includes(
                  `entity_record.${fieldName}`
                )
              ) {
                return dn.id.replace(`${customObjectName}.`, 'entity_record.');
              }
            }
            return dn.id;
          });
        },
      }
    )
  );

  const unavailableMergeFields = notFoundBusinessMergeFields.concat(
    notFoundAutomationMergeFields,
    notFoundMergeFieldsQuery.data ?? []
  );

  const loadingMergeFields = enableOwnMergeFieldsHook
    ? loadingOwnMergeFields || notFoundMergeFieldsQuery.isLoading
    : loadingMergeFieldsProp || notFoundMergeFieldsQuery.isLoading;

  const emailFromDomainEnabled = useEmailFromDomainEnabledEntitlement();

  const {
    id,
    roles: [role],
    first_name,
    last_name,
    email,
    display_name,
  } = useSelector((s) => s.authentication.teamMember);

  const currentTeamMember = useMemo(
    () => ({
      id,
      firstName: first_name,
      lastName: last_name,
      email,
      displayName: display_name,
    }),
    [id, first_name, last_name, email, display_name]
  );
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [senderType, setSenderType] = useState(
    sender?.type || (emailFromDomainEnabled ? TEAM_MEMBER : BUSINESS)
  );
  const [sendFrom, setSendFrom] = useState(sender?.id || id);
  const [sendFromOption, setSendFromOption] = useState();
  const [sendFromTeamMemberField, setSendFromTeamMemberField] = useState(
    initialData?.senderField?.displayName
      ? displayNameToOption(initialData.senderField)
      : initialData.senderField || null
  );
  const [fromName, setFromName] = useState(
    sender?.customName ? CUSTOM : DEFAULT
  );
  const [customName, setCustomName] = useState(sender?.customName || '');
  const [subjectLine, setSubjectLine] = useState(subject || '');
  const [internalName, setInternalName] = useState(name || '');
  const [integratedInbox, setIntegratedInbox] = useState(
    externalAccount
      ? {
          ...externalAccount,
          label: externalAccount?.label || externalAccount?.email,
          value: externalAccount?.value || externalAccount?.id,
        }
      : null
  );

  const [title, setTitle] = useState(internalName || subjectLine);
  const [senderTypeWarning, setSenderTypeWarning] = useState('');
  const [integratedInboxError, setIntegratedInboxError] = useState('');
  const [subjectLineError, setSubjectLineError] = useState('');
  const [customNameError, setCustomNameError] = useState('');
  const [internalNameError, setInternalNameError] = useState('');
  const [sendFromError, setSendFromError] = useState('');
  const [sendFromFieldError, setSendFromFieldError] = useState('');
  const [isConfirmModalOpen, { showModal, hideModal }] = useModalControl();
  const showCustomName = fromName === CUSTOM;
  const mjml2html = useMjml2Html();
  const [, showErrors, flashError] = useFlashTransition();
  const handleSendFromChange = usePathSetter(setSendFrom, 'value');

  const { isLoadingAccounts, accountSelectOptions, accounts } =
    useIntegratedInbox(useAllIntgegratedInboxes);

  const updateIntegratedInboxOnceRef = useRef(false);

  // because we don't have invalid with the integrated inbox
  if (!updateIntegratedInboxOnceRef.current && integratedInbox && accounts) {
    updateIntegratedInboxOnceRef.current = true;

    const option = accounts.find(
      ({ id }) => id === integratedInbox.id || id === integratedInbox.value
    );
    if (option) {
      const maybeInvalidLabel = getInvalidLabel(
        option.emailStatus,
        integratedInbox.label,
        t
      );
      setIntegratedInbox((prev) => ({ ...prev, ...maybeInvalidLabel }));
    }
  }

  const initialHeaderDataRef = useRef({
    senderType: initialData?.sender?.type,
    sendFrom: initialData?.sender?.id || id,
    fromName: initialData?.sender?.customName ? CUSTOM : DEFAULT,
    customName: initialData?.sender?.customName,
    subjectLine: initialData?.subject,
    internalName: initialData?.name,
  });

  const headerData = useMemo(
    () => ({
      senderType,
      sendFrom: [TEAM_MEMBER, TEAM_MEMBER_FIELD, LAST_ROLE].includes(senderType)
        ? sendFrom
        : initialData?.sender?.id || id,
      fromName,
      customName,
      subjectLine,
      internalName,
    }),
    [
      customName,
      senderType,
      sendFrom,
      fromName,
      subjectLine,
      internalName,
      initialData,
      id,
    ]
  );

  const textLinkOptions = useTextLinkOptions(t, !!customObject);
  const isNewUnsavedEmail = useMemo(
    () => !craftJsContent || Object.keys(craftJsContent).length === 0,
    [craftJsContent]
  );

  const { value: rolesOptions } = useAsync(
    TeamMemberService.getRolesOptionsList,
    []
  );

  const adminRole = useMemo(() => {
    return Array.isArray(rolesOptions)
      ? rolesOptions.find((role) => role.label === 'Admin') ?? ''
      : '';
  }, [rolesOptions]);

  const handleFromFieldChange = useCallback((option) => {
    setSendFromTeamMemberField(option);
    setSendFromFieldError('');
  }, []);

  const handleFromNameChange = useCallback(({ value }) => {
    setFromName(value);
    if (value === DEFAULT) {
      setCustomName('');
    }
  }, []);

  const handleSubjectLineChange = useCallback(
    (value) => {
      setSubjectLine(value);
      setSubjectLineError('');
      setInternalNameError('');
    },
    [setSubjectLine, setSubjectLineError]
  );

  const handleCustomNameChange = useCallback(
    (value) => {
      setCustomName(value);
      setCustomNameError('');
    },
    [setCustomName, setCustomNameError]
  );

  const handleInternalName = useCallback(
    (value) => {
      setInternalName(value);
      setInternalNameError('');
    },
    [setInternalNameError]
  );

  const handleIntegratedInboxChange = useCallback(
    (value) => {
      setIntegratedInbox(value);
      setIntegratedInboxError('');
    },
    [setIntegratedInboxError]
  );

  const handleSenderTypeChange = useCallback(
    ({ value }) => {
      let sendFrom = null;
      let sendFromOption = null;

      if (value === TEAM_MEMBER) {
        sendFrom = id;
        sendFromOption = convertToTeamOption(currentTeamMember);
      } else if (value === LAST_ROLE) {
        sendFrom = role ?? adminRole.value;
        sendFromOption =
          rolesOptions.find(({ value }) => value === role) ?? adminRole;
      } else if (value === SPECIFIC_INTEGRATED_INBOX) {
        setFromName(DEFAULT);
        setCustomName('');
      }
      setSendFromOption(sendFromOption);
      setSendFrom(sendFrom);
      setSenderType(value);
      setSenderTypeWarning('');
    },
    [id, currentTeamMember, role, adminRole, rolesOptions]
  );

  const [{ options: teamMembers, ...selectSenderProps }] =
    useSelectTypeaheadWithScroll({
      selectRef: sendFromSelectFrom,
      objectToOption: convertToTeamOption,
      entity: Entities.TeamMember,
      params: {
        is_verified: true,
      },
    });

  const isSendFromVerified = useMemo(
    // Previously, unverified emails were selectable in the Send From dropdown, so it's possilbe the
    // saved send from value on the template is not valid (and appears blank) https://kizen.atlassian.net/browse/KZN-3885
    () => (senderType === SENDER_TYPE.TEAM_MEMBER ? sendFrom ?? false : true),
    [senderType, sendFrom]
  );

  const hasErrors = useCallback(() => {
    const required = t('This field is required.');
    let error = false;

    if (subjectLine.trim().length === 0) {
      setSubjectLineError(required);
      error = true;
    }

    if (showCustomName && customName.trim().length === 0) {
      setCustomNameError(required);
      error = true;
    }
    if (!isSendFromVerified) {
      setSendFromError(required);
      error = true;
    } else if (
      senderType === SENDER_TYPE.TEAM_MEMBER_FIELD &&
      !sendFromTeamMemberField
    ) {
      setSendFromFieldError(required);
      error = true;
    } else if (senderType === SENDER_TYPE.SPECIFIC_INTEGRATED_INBOX) {
      if (!integratedInbox) {
        setIntegratedInboxError(required);
        error = true;
      } else if (integratedInbox.error) {
        setIntegratedInboxError(t('This Integrated Inbox is invalid.'));
        error = true;
      }
    }
    return error;
  }, [
    t,
    subjectLine,
    showCustomName,
    customName,
    isSendFromVerified,
    sendFromTeamMemberField,
    senderType,
    integratedInbox,
  ]);

  const handleSendTestEmail = useCallback(
    async (email) => {
      const { html } = await mjml2html(
        getMjmlJson(canvasRef.current.editor, {
          processDynamicImages: false,
          processDynamicText: false,
        })
      );
      try {
        await LibraryService.sendTestEmail(subjectLine, email, html);
        return true;
      } catch (error) {
        showToast({
          message: t(
            'Failed to send the test email. Please try again or contact Kizen Support if the problem persists.'
          ),
          variant: toastVariant.FAILURE,
        });
        return false;
      }
    },
    [subjectLine, mjml2html, showToast, t]
  );

  const handleSave = useCallback(
    async (shouldClose = false) => {
      if (isTemplateUpdating) {
        return;
      }

      const showBusinessWarning =
        !emailFromDomainEnabled &&
        !default_integrated_inbox?.id &&
        senderType === BUSINESS;

      if (showBusinessWarning) {
        setSenderTypeWarning(
          t('Warning: Business has no default inbox configured.')
        );
      }

      if (hasErrors()) {
        hideModal();
        flashError();
        return;
      }

      // delayed showing the business warning until after the error check
      if (showBusinessWarning) {
        flashError();
      }

      const teamMemberValue = getTeamMemberValue(
        sendFrom,
        senderTeamMember,
        currentTeamMember,
        sendFromOption
      );

      const selectedSummary = getSelectedSummary(
        senderType,
        [teamMemberValue],
        rolesOptions,
        chosenBusinessName,
        sendFrom,
        sendFromTeamMemberField,
        integratedInbox
      );

      const response = await buildResponse(
        canvasRef.current.editor,
        mjml2html,
        {
          senderType,
          sendFrom,
          customName,
          subjectLine,
          internalName,
          selectedSummary,
          integratedInbox,
        }
      );
      try {
        await onConfirm(
          { ...response, baseMessageId: initialData?.baseMessageId },
          shouldClose
        );

        setTitle(internalName || subjectLine);
        // Don't use `response.craftJsContent` to update currentEditsRef - that object will
        // have reassigned image ids and break the is-equal check when clicking 'back'
        if (canvasRef.current?.editor) {
          currentEditsRef.current =
            canvasRef.current.editor?.query?.getSerializedNodes();
        }
        initialHeaderDataRef.current = headerData;
      } catch (err) {
        if (err.internalNameError) {
          hideModal();
          setInternalNameError(err.internalNameError);
          flashError();
        } else if (!err?.skipToast) {
          showToast({
            message: t(
              'Message Template could not be saved. Please try again or contact Kizen support.'
            ),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [
      isTemplateUpdating,
      emailFromDomainEnabled,
      default_integrated_inbox?.id,
      senderType,
      hasErrors,
      sendFrom,
      senderTeamMember,
      currentTeamMember,
      sendFromOption,
      rolesOptions,
      chosenBusinessName,
      sendFromTeamMemberField,
      integratedInbox,
      mjml2html,
      customName,
      subjectLine,
      internalName,
      initialData,
      t,
      hideModal,
      flashError,
      onConfirm,
      headerData,
      showToast,
    ]
  );

  const sendFromOptions = useMemo(() => {
    if (senderType === TEAM_MEMBER) {
      return teamMembers ?? EMPTY_ARRAY;
    }
    return senderType === LAST_ROLE ? rolesOptions : EMPTY_ARRAY;
  }, [senderType, teamMembers, rolesOptions]);

  const modalHeader = useMemo(() => {
    const saveAndClose = async () => {
      await handleSave(true);
    };
    const onGoBack = () => {
      if (
        !areTreesEqual(
          canvasRef.current?.editor?.query.getSerializedNodes(),
          currentEditsRef.current
        ) ||
        !headerDataEq(initialHeaderDataRef.current, headerData)
      ) {
        showModal();
      } else {
        onCancel();
      }
    };
    return (
      <PageBuilderHeader
        title={title}
        saving={!isConfirmModalOpen && isTemplateUpdating}
        rightActionButton={createElement(SendTestEmailButton, {
          onConfirm: handleSendTestEmail,
        })}
        onSave={() => handleSave(false)}
        onSaveAndClose={saveAndClose}
        onGoBack={onGoBack}
      />
    );
  }, [
    isConfirmModalOpen,
    isTemplateUpdating,
    handleSendTestEmail,
    handleSave,
    showModal,
    onCancel,
    headerData,
    title,
  ]);

  const handlers = useMemo(() => {
    return [
      { id: 'sender-type' },
      {
        id: 'sender-from',
      },
      {
        id: 'sender-from-name',
      },
      showCustomName
        ? {
            id: 'sender-from-custom-name',
          }
        : null,
      {
        id: 'subject-line',
      },
      {
        id: 'internal-name',
      },
      {
        id: 'integrated-inbox',
      },
    ].filter(Boolean);
  }, [showCustomName]);

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners(handlers);

  const onAIRequestStart = useCallback(
    (requestType) => {
      maybeTrackAIRequest('email', initialData.id, businessId, requestType);
    },
    [initialData.id, businessId]
  );

  return (
    <>
      {isConfirmModalOpen && (
        <ConfirmExitModal
          heading={
            isNewUnsavedEmail
              ? t('Your Email Is Not Saved')
              : t('Your Email Has Unsaved Changes')
          }
          discardButtonText={isNewUnsavedEmail ? t('Discard Email') : undefined}
          isSaving={isTemplateUpdating}
          onCancel={hideModal}
          onSave={() => handleSave(true)}
          onDiscard={() => {
            hideModal();
            onCancel();
          }}
        >
          {isNewUnsavedEmail
            ? t(
                "This new email has not yet been saved. If you don't save, your email will be lost."
              )
            : t('All unsaved changes will be lost unless you save your email.')}
        </ConfirmExitModal>
      )}
      <FullscreenModal show header={modalHeader} enforceFocus={false}>
        <PageBuilderContainer
          enableAI
          enableDynamicImages
          enableMergeFields
          enableTextLinks
          enableAllObjectMergeFields={enableAllObjectMergeFields}
          unavailableMergeFields={unavailableMergeFields}
          mergeFields={mergeFields}
          loadingMergeFields={loadingMergeFields}
          textLinkOptions={textLinkOptions}
          nodes={emailNodes}
          creatorElements={emailCreatorElements}
          modalLayer={modalLayer}
          onAIRequestStart={onAIRequestStart}
        >
          <PageBuilderContentWrapper>
            <PageBuilderCanvasWrapper>
              <KeyBoardContext.Provider value={{ assignFieldHandle }}>
                <PageBuilderBodyHeader>
                  <SenderInfoContainer>
                    {emailFromDomainEnabled ? (
                      <>
                        <SenderType
                          disabled={readonlySenderInfo}
                          value={senderType}
                          onChange={handleSenderTypeChange}
                          businessName={chosenBusinessName}
                          modalLayer={modalLayer}
                          {...getKeyListenersProps('sender-type')}
                        />
                        <SendFromTeamMemberField
                          show={senderType === TEAM_MEMBER_FIELD}
                          disabled={readonlySenderInfo}
                          sendFromTeamMemberField={sendFromTeamMemberField}
                          customObject={customObject}
                          showError={showErrors}
                          errorMessage={sendFromFieldError}
                          onChange={handleFromFieldChange}
                          modalLayer={modalLayer}
                          keyListenersProps={getKeyListenersProps(
                            'sender-from'
                          )}
                        />
                        {![
                          LAST_ANY,
                          TEAM_MEMBER_FIELD,
                          SPECIFIC_INTEGRATED_INBOX,
                        ].includes(senderType) && senderType !== BUSINESS ? (
                          <SendFrom
                            disabled={readonlySenderInfo}
                            value={getTeamMemberValue(
                              sendFrom,
                              senderTeamMember,
                              currentTeamMember,
                              sendFromOption
                            )}
                            options={sendFromOptions}
                            showError={showErrors}
                            errorMessage={sendFromError}
                            onChange={(opt) => {
                              setSendFromOption(opt);
                              handleSendFromChange(opt);
                            }}
                            modalLayer={modalLayer}
                            {...(senderType === TEAM_MEMBER
                              ? {
                                  ref: sendFromSelectFrom,
                                  ...selectSenderProps,
                                }
                              : {})}
                            {...getKeyListenersProps('sender-from')}
                          />
                        ) : null}
                        {senderType !== SPECIFIC_INTEGRATED_INBOX ? (
                          <StyledFromName
                            value={fromName}
                            onChange={handleFromNameChange}
                            modalLayer={modalLayer}
                            {...getKeyListenersProps('sender-from-name')}
                          />
                        ) : (
                          <StyledIntegratedInbox2
                            value={integratedInbox}
                            onChange={handleIntegratedInboxChange}
                            modalLayer={modalLayer}
                            accountSelectOptions={accountSelectOptions}
                            isLoading={isLoadingAccounts}
                            showError={showErrors}
                            errorMessage={integratedInboxError}
                            {...getKeyListenersProps('integrated-inbox')}
                          />
                        )}
                        {showCustomName && (
                          <StyledCustomName
                            value={customName}
                            errorMessage={customNameError}
                            showError={showErrors}
                            onChange={handleCustomNameChange}
                            {...getKeyListenersProps('sender-from-custom-name')}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <IntegratedInboxSenderType
                          disabled={readonlySenderInfo}
                          value={senderType}
                          onChange={handleSenderTypeChange}
                          businessName={chosenBusinessName}
                          modalLayer={modalLayer}
                          showError={showErrors}
                          errorMessage={senderTypeWarning}
                          isBusinessSenderConfigured={
                            !!default_integrated_inbox?.id
                          }
                          {...getKeyListenersProps('sender-type')}
                        />

                        {senderType === SPECIFIC_INTEGRATED_INBOX ? (
                          <StyledIntegratedInbox2
                            value={integratedInbox}
                            onChange={handleIntegratedInboxChange}
                            modalLayer={modalLayer}
                            accountSelectOptions={accountSelectOptions}
                            isLoadingAccounts={isLoadingAccounts}
                            showError={showErrors}
                            errorMessage={integratedInboxError}
                            {...getKeyListenersProps('integrated-inbox')}
                          />
                        ) : null}
                      </>
                    )}

                    <StyledSubjectLine
                      value={subjectLine}
                      errorMessage={subjectLineError}
                      showError={showErrors}
                      onChange={handleSubjectLineChange}
                      {...getKeyListenersProps('subject-line')}
                    />
                    <StyledInternalName
                      value={internalName}
                      errorMessage={internalNameError}
                      showError={showErrors}
                      onChange={handleInternalName}
                      {...getKeyListenersProps('internal-name')}
                    />
                  </SenderInfoContainer>
                </PageBuilderBodyHeader>
              </KeyBoardContext.Provider>
              <PageBuilderCanvas ref={canvasRef} data={dataRef.current} />
            </PageBuilderCanvasWrapper>
            <PageBuilderTray
              emailMode
              trayItems={emailTrayItems}
              nodeSettings={emailNodeSettings}
            />
          </PageBuilderContentWrapper>
        </PageBuilderContainer>
      </FullscreenModal>
    </>
  );
};
